'use client';

/**
 * Monitor único de qualidade de conexão local.
 * Uma subscrição LiveKit + timeouts (sem setInterval) — evita polling que
 * re-renderiza a árvore React e pressiona CPU/RAM do cliente.
 */

import * as React from 'react';
import { useConnectionQualityIndicator, useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { useEconomyMode } from './economyMode';
import { shouldAutoActivateEconomy } from './screen-share-quality-guard';

/**
 * Antes: 8s de Poor → economia forçada (LOW + 4 tiles). Com score Poor falso
 * (TURN/BWE, 0% perda) isso prendia a sala em qualidade ruim. Só auto-ativa
 * após Poor bem sustentado.
 */
export const AUTO_ECONOMY_THRESHOLD_MS = 25_000;
export const AUTO_ECONOMY_RESTORE_MS = 10_000;
const POOR_BANNER_DELAY_MS = 5_000;
const DISMISS_COOLDOWN_KEY = 'evolumeet-poor-banner-dismissed-at';
const DISMISS_COOLDOWN_MS = 10 * 60 * 1_000;

function isPoorQuality(quality: string): boolean {
  return quality === 'poor' || quality === 'lost';
}

function isDismissedRecently(): boolean {
  if (typeof window === 'undefined') return false;
  const ts = parseInt(localStorage.getItem(DISMISS_COOLDOWN_KEY) ?? '0', 10);
  return Date.now() - ts < DISMISS_COOLDOWN_MS;
}

function markBannerDismissed() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DISMISS_COOLDOWN_KEY, String(Date.now()));
}

/** @deprecated Use ConnectionStabilityMonitor — mantido para compatibilidade de import. */
export function AutoEconomyBridge() {
  return null;
}

export function PoorConnectionBanner() {
  return null;
}

export function ConnectionStabilityMonitor() {
  const room = useRoomContext();
  const { quality } = useConnectionQualityIndicator({ participant: room.localParticipant });
  const { economyModeEnabled, setEconomyModeEnabled } = useEconomyMode();

  const autoActivatedRef = React.useRef(false);
  const activateTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const restoreTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const bannerTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const [showBanner, setShowBanner] = React.useState(false);

  const clearActivateTimer = React.useCallback(() => {
    if (activateTimerRef.current) {
      clearTimeout(activateTimerRef.current);
      activateTimerRef.current = undefined;
    }
  }, []);

  const clearRestoreTimer = React.useCallback(() => {
    if (restoreTimerRef.current) {
      clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = undefined;
    }
  }, []);

  const clearBannerTimer = React.useCallback(() => {
    if (bannerTimerRef.current) {
      clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = undefined;
    }
  }, []);

  const resetAll = React.useCallback(() => {
    clearActivateTimer();
    clearRestoreTimer();
    clearBannerTimer();
    autoActivatedRef.current = false;
    setShowBanner(false);
  }, [clearActivateTimer, clearRestoreTimer, clearBannerTimer]);

  React.useEffect(() => {
    const poor = isPoorQuality(quality);

    if (poor) {
      clearRestoreTimer();

      const screenSharing = room.localParticipant?.isScreenShareEnabled === true;
      if (
        shouldAutoActivateEconomy({
          qualityIsPoor: true,
          screenSharing,
          alreadyEnabled: economyModeEnabled,
        }) &&
        !autoActivatedRef.current &&
        !activateTimerRef.current
      ) {
        activateTimerRef.current = setTimeout(() => {
          activateTimerRef.current = undefined;
          if (
            !shouldAutoActivateEconomy({
              qualityIsPoor: true,
              screenSharing: room.localParticipant?.isScreenShareEnabled === true,
              alreadyEnabled: false,
            })
          ) {
            return;
          }
          autoActivatedRef.current = true;
          setEconomyModeEnabled(true);
        }, AUTO_ECONOMY_THRESHOLD_MS);
      }

      if (!bannerTimerRef.current && !isDismissedRecently() && !economyModeEnabled) {
        bannerTimerRef.current = setTimeout(() => {
          bannerTimerRef.current = undefined;
          if (!isDismissedRecently() && !economyModeEnabled) {
            setShowBanner(true);
          }
        }, POOR_BANNER_DELAY_MS);
      }
    } else {
      clearActivateTimer();
      clearBannerTimer();
      setShowBanner(false);

      if (autoActivatedRef.current && economyModeEnabled && !restoreTimerRef.current) {
        restoreTimerRef.current = setTimeout(() => {
          restoreTimerRef.current = undefined;
          autoActivatedRef.current = false;
          setEconomyModeEnabled(false);
        }, AUTO_ECONOMY_RESTORE_MS);
      }
    }
  }, [
    quality,
    economyModeEnabled,
    setEconomyModeEnabled,
    room,
    clearActivateTimer,
    clearRestoreTimer,
    clearBannerTimer,
  ]);

  React.useEffect(() => {
    if (economyModeEnabled) {
      clearBannerTimer();
      setShowBanner(false);
    }
  }, [economyModeEnabled, clearBannerTimer]);

  React.useEffect(() => {
    const onReconnected = () => resetAll();
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
      resetAll();
    };
  }, [room, resetAll]);

  if (!showBanner || economyModeEnabled) return null;

  const screenSharing = room.localParticipant?.isScreenShareEnabled === true;

  return (
    <div
      className="meet-poor-connection-banner"
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '72px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9500,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'rgba(30, 30, 30, 0.96)',
        color: '#fff',
        borderRadius: '10px',
        padding: '10px 14px',
        fontSize: '13px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        maxWidth: '440px',
        width: 'calc(100vw - 32px)',
        border: '1px solid rgba(234, 179, 8, 0.5)',
      }}
    >
      <span style={{ fontSize: '16px' }}>⚡</span>
      <span style={{ flex: 1 }}>
        {screenSharing
          ? 'Conexão instável com tela compartilhada — o modo economia reduz câmera e tela.'
          : 'Sua conexão está instável.'}
      </span>
      <button
        type="button"
        onClick={() => {
          setShowBanner(false);
          setEconomyModeEnabled(true);
        }}
        style={{
          background: 'rgba(234, 179, 8, 0.9)',
          color: '#1a1a1a',
          border: 'none',
          borderRadius: '6px',
          padding: '5px 10px',
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Ativar modo economia
      </button>
      <button
        type="button"
        onClick={() => {
          markBannerDismissed();
          clearBannerTimer();
          setShowBanner(false);
        }}
        aria-label="Fechar"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          fontSize: '16px',
          padding: '0 4px',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
